import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useState } from "react";

export interface DeleteConfirmDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Number of documents to delete */
  documentCount: number;
  /** Whether delete operation is in progress */
  isDeleting: boolean;
  /** Callback when user confirms deletion */
  onConfirm: (hardDelete: boolean) => void;
  /** Callback when user cancels */
  onCancel: () => void;
}

/**
 * Confirmation dialog for bulk delete operations
 * with option for soft vs hard delete
 */
export function DeleteConfirmDialog({
  open,
  documentCount,
  isDeleting,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) {
  const [hardDelete, setHardDelete] = useState(false);

  const handleConfirm = () => {
    onConfirm(hardDelete);
  };

  const handleClose = () => {
    if (!isDeleting) {
      setHardDelete(false);
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="delete-confirm-dialog-title"
    >
      <DialogTitle
        id="delete-confirm-dialog-title"
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <WarningAmberIcon color="warning" />
        Confirm Delete
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Are you sure you want to delete{" "}
          <strong>
            {documentCount.toLocaleString()} document{documentCount !== 1 ? "s" : ""}
          </strong>
          ?
        </Typography>

        <Box sx={{ mt: 2, p: 2, backgroundColor: "action.hover", borderRadius: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={hardDelete}
                onChange={(e) => setHardDelete(e.target.checked)}
                disabled={isDeleting}
                color="error"
              />
            }
            label={
              <Typography variant="body2">
                <strong>Permanently delete</strong> (cannot be undone)
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
            {hardDelete
              ? "Documents and their associated S3 files will be permanently removed."
              : "Documents will be soft-deleted and can be restored later if needed."}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isDeleting} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: "none" }}
        >
          {isDeleting
            ? "Deleting..."
            : hardDelete
              ? "Permanently Delete"
              : `Delete ${documentCount.toLocaleString()} Document${documentCount !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
